import React, { useCallback, useMemo, useState } from "react";
import { LinearProgress } from "@mui/material";
import { ArrowLeft, Clock3, Pencil, Stethoscope } from "lucide-react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ReplayIcon from "@mui/icons-material/Replay";
import { useSnackbar } from "notistack";
import { useHistory, useLocation } from "react-router-dom";

import Header from "../../../../components/Header";
import Loading from "../../../../components/Loading";
import Button from "../../../../componentsv2/Button";
import Container from "../../../../componentsv2/Container";
import Box from "../../../../componentsv2/Box";
import { api } from "../../../../services/Api";
import getApiErrorMessage from "../../../../utils/getApiErrorMessage";
import ChipTipoProcedimento from "../../Components/ChipTipoProcedimento";
import SecaoCard from "../VisualizarAgendamento/components/SecaoCard";
import ModalRegistrarEvolucao from "./components/ModalRegistrarEvolucao";
import {
  formatarNomeEnfermeiro,
  obterDescricaoEvolucaoSessao,
} from "../../utils/evolucaoFormatters";

function formatarData(valor) {
  if (!valor) return "";
  return new Date(valor).toLocaleDateString("pt-BR");
}

function formatarHora(valor) {
  if (!valor) return "";
  return new Date(valor).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function paraDataInput(valor) {
  const data = valor ? new Date(valor) : new Date();
  return data.toISOString().slice(0, 10);
}

function paraHoraInput(valor) {
  if (!valor) return "";
  const data = new Date(valor);
  return `${String(data.getHours()).padStart(2, "0")}:${String(data.getMinutes()).padStart(2, "0")}`;
}

function resumoBadgeStyle(status) {
  const realizada = status === "REALIZADA";
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px 12px",
    borderRadius: "999px",
    fontSize: "12px",
    lineHeight: 1,
    fontWeight: 700,
    letterSpacing: "0.01em",
    backgroundColor: realizada ? "#05AF3A" : "#D07A00",
    color: "#FFFFFF",
  };
}

function CardSessao({
  sessao,
  onMarcarRealizada,
  onEditarEvolucao,
  onVoltarPendente,
  salvando,
}) {
  const realizada = sessao.status === "REALIZADA";
  const evolucao = obterDescricaoEvolucaoSessao(sessao);

  return (
    <div
      style={{
        border: "1px solid #DDE6F5",
        borderRadius: "12px",
        padding: "12px 14px",
        marginBottom: "12px",
        backgroundColor: "#FFFFFF",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
        <ChipTipoProcedimento tipo={sessao.tipo} mostrarSigla={false} />
        <span
          style={{
            fontSize: "11px",
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: "4px",
            backgroundColor: "#F3F5F8",
            color: "#5C6E91",
            textTransform: "uppercase",
          }}
        >
          {sessao.localLabel}
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <div
          style={{
            fontSize: "16px",
            fontWeight: 700,
            color: "#111111",
            textTransform: "uppercase",
            marginBottom: realizada && evolucao ? "6px" : "10px",
            flex: 1,
          }}
        >
          {sessao.nome}
          {sessao.numeroSessao > 1 ? ` — Sessão ${sessao.numeroSessao}` : ""}
        </div>

        {!realizada && (
          <Button
            type="button"
            variant="text"
            disabled={salvando}
            style={{ minWidth: "auto", padding: 2 }}
            onClick={() => onMarcarRealizada(sessao)}
          >
            <ArrowForwardIosIcon style={{ color: "#9E9E9E", fontSize: 18 }} />
          </Button>
        )}

        {realizada && (
          <>
            <Button
              type="button"
              variant="text"
              disabled={salvando}
              style={{ minWidth: "auto", padding: 2 }}
              onClick={() => onEditarEvolucao(sessao)}
              aria-label="Editar evolução"
            >
              <Pencil size={18} color="#9E9E9E" strokeWidth={2} />
            </Button>
            <Button
              type="button"
              variant="text"
              disabled={salvando}
              style={{ minWidth: "auto", padding: 2 }}
              onClick={() => onVoltarPendente(sessao)}
              aria-label="Voltar para pendente"
            >
              <ReplayIcon style={{ color: "#9E9E9E", fontSize: 18 }} />
            </Button>
          </>
        )}
      </div>

      {realizada && evolucao && (
        <p style={{ margin: "0 0 8px 0", fontSize: "13px", color: "#4E5B84", lineHeight: 1.45 }}>
          {evolucao}
        </p>
      )}

      {realizada && sessao.realizadoEm ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
            fontSize: "12px",
            fontWeight: 600,
            color: "#8D97B3",
            textTransform: "uppercase",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Stethoscope size={14} strokeWidth={1.75} />
            <span>{sessao.enfermeiroNome}</span>
          </div>
          <span>{`${formatarData(sessao.realizadoEm)} ${formatarHora(sessao.realizadoEm)}`}</span>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "12px",
            fontWeight: 600,
            color: "#8D97B3",
            textTransform: "uppercase",
          }}
        >
          <Stethoscope size={14} strokeWidth={1.75} />
          <span>{sessao.enfermeiroNome}</span>
        </div>
      )}
    </div>
  );
}

export default function EvolucaoAgendamento() {
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [agendamento, setAgendamento] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [salvandoSessaoId, setSalvandoSessaoId] = useState("");
  const [modal, setModal] = useState({
    open: false,
    modo: "registrar",
    sessao: null,
    descricao: "",
    data: paraDataInput(),
    hora: "",
  });

  const carregarDados = useCallback(async () => {
    const agendamentoId = location.state?.row?.id;
    if (!agendamentoId) {
      history.push("/agendaCare");
      return;
    }

    try {
      setLoading(true);

      const [resAgendamento, resSessoes] = await Promise.all([
        api.get(`v2/agendaCare/agendamento/${agendamentoId}`),
        api.get(`/v2/agendaCare/agendamento/${agendamentoId}/sessoes`),
      ]);

      setAgendamento(resAgendamento.data?.data || null);
      setGrupos(Array.isArray(resSessoes.data?.data) ? resSessoes.data.data : []);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, "Não foi possível carregar a evolução do atendimento."), {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, history, location.state?.row?.id]);

  React.useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const sessoes = useMemo(() => {
    const enfermeiroPadrao = formatarNomeEnfermeiro(agendamento?.enfermeiro);
    const localLabel = agendamento?.localAtendimento === "DOMICILIO" ? "DOMICILIAR" : (agendamento?.localAtendimento || "—");
    return grupos.flatMap(grupo =>
      (grupo.sessoes || []).map(sessao => ({
        ...sessao,
        nome: grupo.nome,
        tipo: grupo.tipo,
        localLabel,
        enfermeiroNome: sessao?.enfermeiro?.nome || enfermeiroPadrao || "—",
      }))
    );
  }, [agendamento?.enfermeiro, agendamento?.localAtendimento, grupos]);

  const progresso = useMemo(() => {
    const realizadas = sessoes.filter(sessao => sessao.status === "REALIZADA").length;
    return { realizadas, total: sessoes.length };
  }, [sessoes]);

  const percentual = progresso.total > 0 ? (progresso.realizadas / progresso.total) * 100 : 0;
  const progressoConcluido = progresso.total > 0 && progresso.realizadas >= progresso.total;
  const pendentes = progresso.total - progresso.realizadas;

  const sessoesPendentes = useMemo(
    () => sessoes.filter(sessao => sessao.status === "PENDENTE"),
    [sessoes]
  );
  const sessoesRealizadas = useMemo(
    () => sessoes.filter(sessao => sessao.status === "REALIZADA"),
    [sessoes]
  );

  const abrirModalSessao = (sessao, modo) => {
    const dataBase = sessao.realizadoEm || new Date();
    setModal({
      open: true,
      modo,
      sessao,
      descricao: obterDescricaoEvolucaoSessao(sessao),
      data: paraDataInput(dataBase),
      hora: paraHoraInput(dataBase),
    });
  };

  const fecharModal = () => {
    setModal({
      open: false,
      modo: "registrar",
      sessao: null,
      descricao: "",
      data: paraDataInput(),
      hora: "",
    });
  };

  const salvarEvolucaoModal = async () => {
    const agendamentoId = location.state?.row?.id;
    const sessao = modal.sessao;
    if (!agendamentoId || !sessao) return;

    const descricao = modal.descricao.trim();
    if (!modal.data || !modal.hora) {
      enqueueSnackbar("Informe data e hora da realização.", { variant: "warning" });
      return;
    }

    const realizadoEm = new Date(`${modal.data}T${modal.hora}:00`);
    if (Number.isNaN(realizadoEm.getTime())) {
      enqueueSnackbar("Data ou hora inválida.", { variant: "warning" });
      return;
    }

    const editando = modal.modo === "editar";

    try {
      setSalvandoSessaoId(sessao.id);
      await api.patch(`/v2/agendaCare/agendamento/${agendamentoId}/sessoes/${sessao.id}`, {
        status: "REALIZADA",
        descricao,
        realizadoEm: realizadoEm.toISOString(),
        enfermeiroId: sessao.enfermeiroId || agendamento?.enfermeiro?.id || null,
      });
      fecharModal();
      await carregarDados();
      enqueueSnackbar(
        editando ? "Evolução atualizada." : "Sessão marcada como realizada.",
        { variant: "success" }
      );
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(
          error,
          editando
            ? "Não foi possível atualizar a evolução da sessão."
            : "Não foi possível registrar a evolução da sessão."
        ),
        { variant: "error" }
      );
    } finally {
      setSalvandoSessaoId("");
    }
  };

  const voltarParaPendente = async sessao => {
    const agendamentoId = location.state?.row?.id;
    if (!agendamentoId) return;

    try {
      setSalvandoSessaoId(sessao.id);
      await api.patch(`/v2/agendaCare/agendamento/${agendamentoId}/sessoes/${sessao.id}`, {
        status: "PENDENTE",
        descricao: obterDescricaoEvolucaoSessao(sessao),
      });
      await carregarDados();
      enqueueSnackbar("Sessão voltou para pendente.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, "Não foi possível voltar a sessão para pendente."), {
        variant: "error",
      });
    } finally {
      setSalvandoSessaoId("");
    }
  };

  const nomePaciente = agendamento?.cliente?.nome || location.state?.row?.cliente?.nome || "—";
  const dataAgendada = agendamento?.data || location.state?.row?.data;
  const horaAgendada = agendamento?.hora || location.state?.row?.hora;
  const usuarioAgendamento = agendamento?.usuario?.username || location.state?.row?.usuario?.username || "—";
  const dataTexto = formatarData(dataAgendada) || "—";
  const horaTexto = horaAgendada || "—";
  const textoCriacao = `Agendado para ${dataTexto} às ${horaTexto} por ${usuarioAgendamento}`;

  return (
    <>
      <Header titulo="Evolução do Atendimento" />

      <Container maxWidth={false} sx={{ px: { xs: 3, md: 6, lg: 8 }, pt: 3, pb: 5, bgcolor: "#F5F5F5" }}>
        <Button onClick={() => history.push({ pathname: "/agendaCare" })}>
          <ArrowLeft size={22} color="#003399" strokeWidth={2} />
        </Button>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2, maxWidth: 1220, mx: "auto", width: "100%" }}>
          <SecaoCard titulo="Atendimento" centralizarConteudo={false}>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <h2 style={{ margin: 0, fontSize: "30px", color: "#101B43" }}>{nomePaciente}</h2>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#9AA3B2", fontSize: "12px" }}>
                <Clock3 size={14} />
                <span>{textoCriacao}</span>
              </div>
            </div>
          </SecaoCard>

          <SecaoCard titulo="Progresso" centralizarConteudo={false}>
            <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#333344", textAlign: "left" }}>
              {progresso.realizadas} de {progresso.total} procedimentos realizados
            </p>

            <LinearProgress
              variant="determinate"
              value={percentual}
              sx={{
                height: 6,
                borderRadius: 4,
                backgroundColor: "#E0E0E0",
                marginBottom: "16px",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: progressoConcluido ? "#43A047" : "#003399",
                },
              }}
            />

            <div style={{ display: "flex", gap: "10px", marginBottom: "4px", flexWrap: "wrap", justifyContent: "flex-start" }}>
              <span style={resumoBadgeStyle("REALIZADA")}>
                {progresso.realizadas} Realizados
              </span>
              <span style={resumoBadgeStyle("PENDENTE")}>
                {pendentes} Pendentes
              </span>
            </div>
          </SecaoCard>

          <SecaoCard titulo="Evolução do pedido" centralizarConteudo={false}>
            {!loading && progresso.total === 0 && (
              <p style={{ color: "#666", fontSize: "14px", margin: 0 }}>
                Nenhuma sessão para procedimentos ou medicamentos neste agendamento. Deslocamentos,
                adicionais e itens N/A não geram sessões.
              </p>
            )}

            {!loading && progresso.total > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: "18px" }}>
                <div>
                  <h5 style={{ margin: "0 0 10px 0", fontSize: "22px", fontWeight: 700, lineHeight: 1.15, color: "#111111" }}>
                    Sessões pendentes
                  </h5>
                  {sessoesPendentes.length === 0 ? (
                    <p style={{ fontSize: "13px", color: "#999" }}>Nenhuma sessão pendente.</p>
                  ) : (
                    sessoesPendentes.map(sessao => (
                      <CardSessao
                        key={sessao.id}
                        sessao={sessao}
                        onMarcarRealizada={sessao => abrirModalSessao(sessao, "registrar")}
                        onEditarEvolucao={sessao => abrirModalSessao(sessao, "editar")}
                        onVoltarPendente={voltarParaPendente}
                        salvando={salvandoSessaoId === sessao.id}
                      />
                    ))
                  )}
                </div>

                <div>
                  <h5 style={{ margin: "0 0 10px 0", fontSize: "22px", fontWeight: 700, lineHeight: 1.15, color: "#111111" }}>
                    Sessões realizadas
                  </h5>
                  {sessoesRealizadas.length === 0 ? (
                    <p style={{ fontSize: "13px", color: "#999" }}>Nenhuma sessão realizada.</p>
                  ) : (
                    sessoesRealizadas.map(sessao => (
                      <CardSessao
                        key={sessao.id}
                        sessao={sessao}
                        onMarcarRealizada={sessao => abrirModalSessao(sessao, "registrar")}
                        onEditarEvolucao={sessao => abrirModalSessao(sessao, "editar")}
                        onVoltarPendente={voltarParaPendente}
                        salvando={salvandoSessaoId === sessao.id}
                      />
                    ))
                  )}
                </div>
              </div>
            )}
          </SecaoCard>
        </Box>
      </Container>

      <ModalRegistrarEvolucao
        open={modal.open}
        modo={modal.modo}
        descricao={modal.descricao}
        data={modal.data}
        hora={modal.hora}
        salvando={!modal.sessao || salvandoSessaoId === modal.sessao?.id}
        onClose={fecharModal}
        onDescricaoChange={descricao => setModal(prev => ({ ...prev, descricao }))}
        onDataChange={data => setModal(prev => ({ ...prev, data }))}
        onHoraChange={hora => setModal(prev => ({ ...prev, hora }))}
        onRegistrar={salvarEvolucaoModal}
      />

      {loading && <Loading />}
    </>
  );
}

